/* BK Flipbook: <bk-flipbook> custom element. Shows one spread at a time (one page on phones)
   and turns pages with a 3D page-turn: on a spread the page lifts at the fold and lands on the
   other side, on a phone the page peels away to the left. */
(() => {
  if (customElements.get('bk-flipbook')) return;

  const EASE = 'cubic-bezier(0.45, 0.05, 0.3, 1)';
  const el = (cls) => {
    const d = document.createElement('div');
    d.className = cls;
    return d;
  };

  class BkFlipbook extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.stage = this.querySelector('[data-stage]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.count = this.querySelector('[data-count]');
      this.mq = matchMedia('(max-width: 749px)');
      this.still = matchMedia('(prefers-reduced-motion: reduce)');
      this.split = this.mq.matches;
      this.page = 0;

      this.prevBtn.addEventListener('click', () => this.go(this.page - 1));
      this.nextBtn.addEventListener('click', () => this.go(this.page + 1));
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.page - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.page + 1); }
      });
      this.bindSwipe();
      this.mq.addEventListener('change', () => this.setSplit(this.mq.matches));
      this.slides.forEach((s) => s.querySelectorAll('img').forEach((i) => (i.draggable = false)));

      this.classList.toggle('is-split', this.split);
      this.show(0);
      this.reveal();
    }

    get pageCount() { return this.slides.length * (this.split ? 2 : 1); }

    spreadOf(p) { return this.split ? Math.floor(p / 2) : p; }

    srcOf(p) {
      const img = this.slides[this.spreadOf(p)].querySelector('img');
      return img.currentSrc || img.src;
    }

    /* The book rises in the first time it scrolls into view. */
    reveal() {
      if (this.still.matches || !('IntersectionObserver' in window)) return;
      this.classList.add('is-armed');
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.classList.add('is-in');
        io.disconnect();
      }, { threshold: 0.2 });
      io.observe(this.stage);
    }

    setSplit(split) {
      if (split === this.split) return;
      this.endTurn();
      const spread = this.spreadOf(this.page);
      this.split = split;
      this.classList.toggle('is-split', split);
      this.show(split ? spread * 2 : spread);
    }

    show(p) {
      this.page = Math.min(Math.max(p, 0), this.pageCount - 1);
      const spread = this.spreadOf(this.page);
      this.slides.forEach((s, i) => {
        const active = i === spread;
        s.classList.toggle('is-active', active);
        s.classList.toggle('is-right', active && this.split && this.page % 2 === 1);
        s.setAttribute('aria-hidden', String(!active));
        /* warm the neighbours so a turn never lands on a blank page */
        if (Math.abs(i - spread) === 1) s.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      });
      this.prevBtn.disabled = this.page === 0;
      this.nextBtn.disabled = this.page === this.pageCount - 1;
      this.count.textContent = (this.count.dataset.label || 'Page [n] of [total]')
        .replace('[n]', this.page + 1)
        .replace('[total]', this.pageCount);
    }

    go(p) {
      if (p < 0 || p >= this.pageCount || p === this.page) return;
      this.endTurn();
      if (this.still.matches) return this.show(p);
      this.turn(p);
    }

    /* One page face: half of a spread image ('l' or 'r'), with a shade for the lighting. */
    face(src, half) {
      const f = el(`bk-fb__face is-${half}`);
      const img = new Image();
      img.src = src;
      img.alt = '';
      img.draggable = false;
      f.append(img, el('bk-fb__shade'));
      return f;
    }

    leaf(front, back, pos) {
      const l = el(`bk-fb__leaf ${pos}`);
      front.classList.add('is-front');
      back.classList.add('is-back');
      l.append(front, back);
      return l;
    }

    /* The target page is put underneath straight away; a layer on top holds the part of the
       old page that is still showing, plus the turning leaf. */
    turn(p) {
      const from = this.page;
      const fwd = p > from;
      const fromSrc = this.srcOf(from);
      const toSrc = this.srcOf(p);
      const layer = el('bk-fb__flip');
      let leaf, angles;

      if (!this.split) {
        const rest = this.face(fromSrc, fwd ? 'l' : 'r');
        rest.classList.add('bk-fb__rest', fwd ? 'at-l' : 'at-r');
        leaf = this.leaf(this.face(fromSrc, fwd ? 'r' : 'l'), this.face(toSrc, fwd ? 'l' : 'r'), fwd ? 'at-r' : 'at-l');
        angles = fwd ? [0, -180] : [0, 180];
        layer.append(rest, leaf);
      } else {
        const half = (q) => (q % 2 ? 'r' : 'l');
        const paper = el('bk-fb__face bk-fb__paper');
        paper.append(el('bk-fb__shade'));
        if (fwd) {
          /* the current page peels away to the left */
          leaf = this.leaf(this.face(fromSrc, half(from)), paper, 'is-full');
          angles = [0, -180];
          layer.append(leaf);
        } else {
          /* the previous page comes back from the left and lands on the current one */
          const rest = this.face(fromSrc, half(from));
          rest.classList.add('bk-fb__rest', 'is-full');
          leaf = this.leaf(this.face(toSrc, half(p)), paper, 'is-full');
          angles = [-180, 0];
          layer.append(rest, leaf);
        }
      }

      this.show(p);
      this.stage.append(layer);
      this.stage.classList.add('is-turning');

      const ms = this.split ? 650 : 900;
      const anims = [
        leaf.animate(
          /* the page lifts off the book on its way over, like paper does */
          [
            { transform: `rotateY(${angles[0]}deg)` },
            { transform: `translateZ(${this.split ? 16 : 24}px) rotateY(${(angles[0] + angles[1]) / 2}deg)` },
            { transform: `rotateY(${angles[1]}deg)` },
          ],
          { duration: ms, easing: EASE, fill: 'forwards' }
        ),
        ...[...leaf.querySelectorAll('.bk-fb__shade')].map((s) =>
          s.animate([{ opacity: 0 }, { opacity: 0.32 }, { opacity: 0 }], { duration: ms, easing: EASE })
        ),
      ];
      const state = { layer, anims };
      this.turning = state;
      anims[0].onfinish = () => this.turning === state && this.endTurn();
    }

    endTurn() {
      const t = this.turning;
      if (!t) return;
      this.turning = null;
      t.anims.forEach((a) => a.cancel());
      t.layer.remove();
      this.stage.classList.remove('is-turning');
    }

    bindSwipe() {
      let x0 = null, y0 = 0;
      this.stage.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; });
      this.stage.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.page + (dx < 0 ? 1 : -1));
      });
      this.stage.addEventListener('pointercancel', () => (x0 = null));
    }
  }

  customElements.define('bk-flipbook', BkFlipbook);
})();
